import { API_CONFIG } from '../config/apiConfig';

// Interface for request options
interface RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  endpoint: string;
  params?: Record<string, string | number>;
  data?: any;
  headers?: Record<string, string>;
  useProxy?: boolean;
}

// Basic encryption function for localStorage (better than storing in plain text)
const encryptLocalData = (data: string): string => {
  return btoa(data); // Base64 encoding - basic but better than nothing for PWA
};

// Basic decryption function
const decryptLocalData = (encrypted: string): string => {
  try {
    return atob(encrypted); // Base64 decoding
  } catch (e) {
    return encrypted; // Return as is if it's not properly encoded
  }
};

// Class to manage API communication
class APIService {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private useCorsProxy: boolean;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    this.useCorsProxy = false;

    // Initialize settings
    this.initialize();
  }
  
  // Initialize with localStorage settings
  initialize() {
    const { baseURL } = API_CONFIG.initialize();
    
    // IMPORTANT: Check for HeaderToken first (as used in C# example)
    const encryptedHeaderToken = localStorage.getItem('HeaderToken');
    const encryptedApiKey = localStorage.getItem('api_key');
    const encryptedAuthToken = localStorage.getItem('api_auth_token');
    
    this.useCorsProxy = localStorage.getItem('use_cors_proxy') === 'true';
    
    if (baseURL) {
      this.baseURL = baseURL;
      console.log('API baseURL initialized');
    }
    
    // Try to get token from all possible sources
    if (encryptedHeaderToken) {
      this.defaultHeaders['HeaderToken'] = decryptLocalData(encryptedHeaderToken);
      console.log('Using HeaderToken for authentication');
    } else if (encryptedApiKey) {
      this.defaultHeaders['HeaderToken'] = decryptLocalData(encryptedApiKey);
      console.log('Using API key for authentication');
    } else if (encryptedAuthToken) {
      this.defaultHeaders['HeaderToken'] = decryptLocalData(encryptedAuthToken);
      console.log('Using auth token for authentication');
    }
    
    // Log token status
    if (this.defaultHeaders['HeaderToken']) {
      console.log('Token loaded successfully, length:', this.defaultHeaders['HeaderToken'].length);
    } else {
      console.warn('No HeaderToken found for API requests');
    }
  }
  
  // Update API settings
  async updateConfig(baseURL: string, apiKey: string, useCorsProxy?: boolean, baseUrlSource: 'manual' | 'auto' = 'manual') {
    try {
      // Validate URL format if provided
      if (baseURL) {
        try {
          new URL(baseURL); // This will throw if invalid
          // Fonte única do baseURL
          const normalized = API_CONFIG.setBaseURL(baseURL, baseUrlSource, true);
          this.baseURL = normalized;
          console.log('API baseURL updated to:', baseURL);
        } catch (error) {
          console.error('Invalid API URL format:', error);
          throw new Error('Formato de URL inválido. Use https://example.com');
        }
      }
      
      if (useCorsProxy !== undefined) {
        this.useCorsProxy = useCorsProxy;
        localStorage.setItem('use_cors_proxy', useCorsProxy ? 'true' : 'false');
        console.log('CORS proxy usage updated:', this.useCorsProxy);
      }
      
      if (apiKey) {
        this.defaultHeaders['HeaderToken'] = apiKey;
        // Encrypt key before storing
        localStorage.setItem('api_key', encryptLocalData(apiKey));
        // Also store as HeaderToken for consistency with C# example
        localStorage.setItem('HeaderToken', encryptLocalData(apiKey));
        console.log('API key updated and encrypted');
      } else {
        // If no apiKey was provided, check if we have an auth token in localStorage
        const encryptedHeaderToken = localStorage.getItem('HeaderToken');
        const encryptedAuthToken = localStorage.getItem('api_auth_token');
        
        if (encryptedHeaderToken) {
          this.defaultHeaders['HeaderToken'] = decryptLocalData(encryptedHeaderToken);
          console.log('Using existing HeaderToken');
        } else if (encryptedAuthToken) {
          this.defaultHeaders['HeaderToken'] = decryptLocalData(encryptedAuthToken);
          // Also update HeaderToken storage
          localStorage.setItem('HeaderToken', encryptedAuthToken);
          console.log('Using existing auth token and saving as HeaderToken');
        } else {
          delete this.defaultHeaders['HeaderToken'];
          console.log('No token available, cleared HeaderToken');
        }
      }
      
      console.log('API config updated successfully', {
        baseURL: this.baseURL,
        hasHeaderToken: !!this.defaultHeaders['HeaderToken'],
        tokenLength: this.defaultHeaders['HeaderToken'] ? this.defaultHeaders['HeaderToken'].length : 0,
        useCorsProxy: this.useCorsProxy
      });
    } catch (error) {
      console.error('Error updating API config:', error);
      throw error; // Re-throw to let caller handle it
    }
  }

  // Method to build the full URL (with or without proxy)
  private buildFullUrl(endpoint: string, params?: Record<string, string | number>, useProxy?: boolean): string {
    try {
      // Substitute parameters in the URL if necessary
      let url = endpoint;
      const usedPathParams = new Set<string>();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          const token = `:${key}`;
          if (url.includes(token)) {
            url = url.replace(token, String(value));
            usedPathParams.add(key);
          }
        });
      }
      
      let fullUrl = `${this.baseURL}${url}`;
      
      // Validate URL before proceeding
      try {
        const parsed = new URL(fullUrl);

        // Append remaining params as query string (e.g. ?vendaID=123)
        if (params) {
          for (const [key, value] of Object.entries(params)) {
            if (usedPathParams.has(key)) continue;
            parsed.searchParams.set(key, String(value));
          }
          fullUrl = parsed.toString();
        }
      } catch (error) {
        console.error('Invalid full URL:', fullUrl);
        throw new Error(`URL inválida: ${fullUrl}`);
      }
      
      // Use proxy CORS if enabled (global or per request)
      const shouldUseProxy = useProxy !== undefined ? useProxy : this.useCorsProxy;

      // Nunca use proxy para destino local (corsproxy não acessa seu localhost)
      try {
        const parsed = new URL(fullUrl);
        const hostname = parsed.hostname;
        const isLoopback =
          hostname === 'localhost' ||
          hostname === '127.0.0.1' ||
          hostname === '0.0.0.0' ||
          hostname === '::1';
        if (isLoopback) {
          return fullUrl;
        }
      } catch {
        // ignore
      }
      
      if (shouldUseProxy) {
        return `${API_CONFIG.corsProxyURL}${encodeURIComponent(fullUrl)}`;
      }
      
      return fullUrl;
    } catch (error) {
      console.error('Error building URL:', error);
      throw error;
    }
  }

  // Method to make requests with fetch and fallback to XHR
  async request<T = any>(options: RequestOptions): Promise<T> {
    const { method, endpoint, params, data, headers, useProxy } = options;
    
    const fullUrl = this.buildFullUrl(endpoint, params, useProxy);
    
    // Verify if there is a decrypted authentication token
    const encryptedAuthToken = localStorage.getItem('api_auth_token');
    const requestHeaders = {
      ...this.defaultHeaders,
      ...headers,
    };
    
    if (encryptedAuthToken && !requestHeaders['HeaderToken']) {
      requestHeaders['HeaderToken'] = decryptLocalData(encryptedAuthToken);
    }
    
    // Log minimum - do not display sensitive data
    console.log(`Making request to: ${fullUrl.split('?')[0]}`); // Remove query params that may contain sensitive data
    
    try {
      // Try first with fetch
      const response = await fetch(fullUrl, {
        method,
        headers: requestHeaders,
        body: data ? JSON.stringify(data) : undefined,
        mode: 'cors',
        credentials: 'include',
        signal: AbortSignal.timeout(API_CONFIG.timeout),
      });

      if (!response.ok) {
        console.error(`Error in fetch request: ${response.status} ${response.statusText}`);
        
        // Try with XHR as a fallback
        return this.requestWithXHR<T>({ method, url: fullUrl, data, headers: requestHeaders });
      }

      const result = await response.json();
      // Log minimum - do not display sensitive data in response
      console.log(`API response received: ${endpoint}`);
      return result;
    } catch (error) {
      console.error('Error in fetch call');
      
      // Try with XHR as a fallback
      return this.requestWithXHR<T>({ method, url: fullUrl, data, headers: requestHeaders });
    }
  }
  
  // Method to make requests with XHR as a fallback for CORS issues
  private async requestWithXHR<T = any>({ 
    method, 
    url, 
    data, 
    headers 
  }: { 
    method: string; 
    url: string; 
    data?: any; 
    headers?: Record<string, string> 
  }): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // For CORS issues, use 'GET' for the OPTIONS method
      xhr.open(method, url, true);
      
      // Define headers
      if (headers) {
        Object.entries(headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
      }
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const result = JSON.parse(xhr.responseText);
            resolve(result);
          } catch (error) {
            reject(new Error(`Error parsing JSON response: ${error}`));
          }
        } else {
          reject(new Error(`Error in request: ${xhr.status} ${xhr.statusText}`));
        }
      };
      
      xhr.onerror = function() {
        console.error('Network error in XHR request - possible CORS issue');
        // Não habilitar proxy automaticamente; isso muda o comportamento sem o usuário.
        reject(new Error('Network error in XHR request - possible CORS issue'));
      };
      
      xhr.ontimeout = function() {
        reject(new Error('Timeout in XHR request'));
      };
      
      xhr.timeout = API_CONFIG.timeout;
      
      // Send data
      if (data) {
        xhr.send(JSON.stringify(data));
      } else {
        xhr.send();
      }
    });
  }

  // Helper methods
  async get<T = any>(endpoint: string, params?: Record<string, string | number>, headers?: Record<string, string>, useProxy?: boolean): Promise<T> {
    return this.request<T>({ method: 'GET', endpoint, params, headers, useProxy });
  }

  async post<T = any>(endpoint: string, data: any, params?: Record<string, string | number>, headers?: Record<string, string>, useProxy?: boolean): Promise<T> {
    return this.request<T>({ method: 'POST', endpoint, data, params, headers, useProxy });
  }

  async put<T = any>(endpoint: string, data: any, params?: Record<string, string | number>, headers?: Record<string, string>, useProxy?: boolean): Promise<T> {
    return this.request<T>({ method: 'PUT', endpoint, data, params, headers, useProxy });
  }

  async delete<T = any>(endpoint: string, params?: Record<string, string | number>, headers?: Record<string, string>, useProxy?: boolean): Promise<T> {
    return this.request<T>({ method: 'DELETE', endpoint, params, headers, useProxy });
  }

  // Get the base URL
  getBaseUrl(): string {
    return this.baseURL;
  }
}

// Export a single instance of the service
export const apiService = new APIService();
